//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import angular, { ILogService, IQService } from 'angular';
import { INgRedux } from 'ng-redux';
import _ from 'underscore';

import {
  CtaButtonStyle,
  IconStyle,
  Pagination,
  PlatformType,
} from '@apis/experiences/experiencesApiModels';
import ExperienceContainerId from '@models/ExperienceContainerId';
import { ExperienceContainerType } from '@models/ExperienceContainerType';
import { ExperienceLayoutType } from '@models/ExperienceLayoutType';
import { SessionVarsService } from '@services/session-vars-service';
import 'app/angular-apis/galleriesApi.service';
import * as Constants from 'app/constants/fanreel-self-serve-customizations';
import { Locales } from 'app/constants/locales/locale';

import * as DefaultLocaleStrings from 'app/constants/locales/defaultLocaleStrings';

import ExperiencesApiService from 'app/services/experiences-api-service';
import ExperiencesService from 'app/services/experiences-service';
import { FanreelCustomizationValue } from '@legacy-components/fanreel-self-serve-customization/fanreel-customization-controls/fanreel-customization-value';
import {
  convertGalleryToExperience,
  FanreelCustomizationAction,
  FanreelCustomizationActionType,
  FanreelCustomizationCarouselNavigationArrowStyle,
  FanreelCustomizationCtaType,
  FanreelCustomizationImageAspectRatios,
  FanreelCustomizationPages,
  HoverStateTheme,
  HoverThemeColors,
  ModalColors,
} from '@legacy-components/fanreel-self-serve-customization/fanreel-customization-types';

import {
  IGallery,
  ISelfServeGallery,
} from '@apis/galleries/galleriesApiModels';
import { galleriesDataActions } from '@galleries/reducers/galleriesDataSlice';
import getSelectedGallery from '@galleries/selectors/getSelectedGallery';
import { isSelfServeGallery } from '@galleries/utils/galleryTypeUtils';
import { isInstaShopGallery } from '@galleries/utils/isInstaShopGallery';
import { ICuralateStore } from '@models/AppState';
import { debounce } from '@utils/debounce';
import { isDefined, isNotNull, isUndefined } from '@utils/filterPredicates';
import { GalleriesApiService } from 'app/angular-apis/galleriesApi.service';
import { ReduxDispatch } from 'app/curalate.store';
import { actions as customizationActions } from '@galleries/actions/galleriesCustomizationActions';
import { bindActionCreators } from 'redux';
import '@legacy-components/fanreel-self-serve-customization/experience-customization-defaults';
import { shouldUseDarkText } from '@utils/styleUtils';

export default interface FanreelCustomizationService
  extends ReturnType<(typeof FanreelCustomizationService)['mapStateToProps']>,
    ReturnType<(typeof FanreelCustomizationService)['mapDispatchToProps']> {}

export default class FanreelCustomizationService {
  public static mapStateToProps = (state: ICuralateStore) => ({
    galleriesData: state.galleries.data,
    selectedGallery: getSelectedGallery(state),
    isGalleriesEnabled: state.session.features.galleries,
    isLocalizationEnabled:
      state.session.features.galleries_self_serve_localization,
    locales: state.galleries.data.sites[0]?.locales,
    showValidationStatus: state.galleries.customization.showValidationStatus,
    isLocalizationAlertHidden:
      state.galleries.customization.isLocalizationAlertHidden,
  });

  public static mapDispatchToProps = (dispatch: ReduxDispatch) => ({
    fetchGalleries: () => dispatch(galleriesDataActions.getGalleries()),
    customizations: bindActionCreators(customizationActions, dispatch),
  });

  private actionsSubject = null;
  private fanreelCustomizationSubject = null;
  private isGalleriesMode: boolean;

  private initialObject;
  private unsubscribe: () => void;
  private isInstaShopGallery: boolean;

  private readonly PAGE_ORDER: FanreelCustomizationPages[];
  private readonly STARTING_PAGE: FanreelCustomizationPages;

  private generatePreviewFromOptions = debounce(
    (
      options,
      overrideCustomizations,
      containerId: ExperienceContainerId,
      locale,
    ) => {
      if (!this.initialObject) {
        this.initialObject = angular.copy(options);
      }

      if (angular.equals(this.initialObject, options)) {
        this.actionsSubject.next(
          new FanreelCustomizationAction(
            FanreelCustomizationActionType.UPDATE_HAS_UNSAVED_CHANGES,
            false,
          ),
        );
      } else {
        this.actionsSubject.next(
          new FanreelCustomizationAction(
            FanreelCustomizationActionType.UPDATE_HAS_UNSAVED_CHANGES,
            true,
          ),
        );
      }

      let request;

      if (overrideCustomizations) {
        request = this.computeDataSourceRequestFromState(options);
      } else {
        request = this.computeUpdateRequestFromState(options, containerId);
        request.locale = locale;
      }

      this.startAsyncAction(FanreelCustomizationActionType.GENERATE_PREVIEW);

      this.experiencesApiService
        .getExperiencePreview(containerId, request)
        .then((response) => {
          this.actionsSubject.next(
            new FanreelCustomizationAction(
              FanreelCustomizationActionType.SET_PREVIEW_SNIPPET,
              response,
            ),
          );
        })
        .finally(() => {
          this.stopAsyncAction(FanreelCustomizationActionType.GENERATE_PREVIEW);
        });
    },
    100,
  );

  constructor(
    private $q: IQService,
    private $log: ILogService,
    private errorNotificationService,
    private experiencesApiService: ExperiencesApiService,
    private ExperienceCustomizationDefaults,
    private experiencesService: ExperiencesService,
    private sessionVarsService: SessionVarsService,
    private galleriesApi: GalleriesApiService,
    private rx,
    private $ngRedux: INgRedux,
  ) {
    this.actionsSubject = new rx.Subject();
    this.fanreelCustomizationSubject = new rx.BehaviorSubject({});

    this.unsubscribe = $ngRedux.connect(
      FanreelCustomizationService.mapStateToProps,
      FanreelCustomizationService.mapDispatchToProps,
    )(this);

    this.isInstaShopGallery =
      this.selectedGallery && isInstaShopGallery(this.selectedGallery);
    this.isGalleriesMode = this.isGalleriesEnabled || this.isInstaShopGallery;

    this.PAGE_ORDER = this.isGalleriesMode
      ? [
          FanreelCustomizationPages.LAYOUT,
          FanreelCustomizationPages.CUSTOMIZE,
          ...(this.isInstaShopGallery
            ? []
            : [FanreelCustomizationPages.UPLOADER]),
          ...(!this.isLocalizationEnabled
            ? []
            : [FanreelCustomizationPages.LOCALIZE]),
        ]
      : [
          FanreelCustomizationPages.MEDIA,
          FanreelCustomizationPages.LAYOUT,
          FanreelCustomizationPages.CUSTOMIZE,
          FanreelCustomizationPages.UPLOADER,
          ...(this.isLocalizationEnabled
            ? []
            : [FanreelCustomizationPages.LOCALIZE]),
          FanreelCustomizationPages.SETUP,
        ];

    this.STARTING_PAGE = this.PAGE_ORDER[0];

    this.actionsSubject
      .scan((state, actionUntyped) => {
        const action: FanreelCustomizationAction =
          actionUntyped as FanreelCustomizationAction;
        return this.scanLoop(state, action);
      }, {})
      .subscribe(this.fanreelCustomizationSubject);
  }

  public deployFanreel(
    containerId: ExperienceContainerId,
    containerType: ExperienceContainerType,
  ) {
    this.startAsyncAction(FanreelCustomizationActionType.DEPLOY);
    const payload = { containerId, containerType };
    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.DEPLOY,
        payload,
      ),
    );
  }

  public showCustomisationSaveWarning() {
    if (this.isLocalizationAlertHidden) {
      this.customizations.openLocalizationWarning();
    }
  }

  public resetLocalisationValidationStatusFlag() {
    if (this.showValidationStatus) {
      this.customizations.hideCustomizationPanelErrorStatus();
    }
  }

  public getFanreelCustomizationObservable() {
    return this.fanreelCustomizationSubject.asObservable();
  }

  public generatePreview(
    customizationOptions,
    overrideCustomizations,
    containerId: ExperienceContainerId,
  ) {
    const payload = {
      customizationOptions,
      overrideCustomizations,
      containerId,
    };
    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.GENERATE_PREVIEW,
        payload,
      ),
    );
  }

  public async loadDataFromGalleries(containerId: string) {
    const targetGallery = this.galleriesData.galleries.byId[containerId];
    if (targetGallery) {
      // Nobody should be calling this with non-self-serve-galleries.
      return Promise.resolve(
        convertGalleryToExperience(targetGallery as ISelfServeGallery),
      );
    } else {
      const galleries = await this.fetchGalleries();

      const matchesSelfServeContainerId = (
        gallery: IGallery,
      ): gallery is ISelfServeGallery => {
        return (
          gallery.containerId === containerId && isSelfServeGallery(gallery)
        );
      };
      const selectedGallery = galleries.find(matchesSelfServeContainerId);

      return convertGalleryToExperience(selectedGallery);
    }
  }

  public loadDataFromExperiences(containerId: string) {
    return this.experiencesService
      .getExperiencesPromise()
      .then((newExperiencesState) => {
        return _(newExperiencesState.experiences).findWhere({
          experienceType: containerId,
        });
      });
  }

  public loadData(
    containerId: string,
    mediaSource?: string,
    dataSourceId?: string,
  ) {
    const experiencePromise = this.isGalleriesMode
      ? this.loadDataFromGalleries(containerId)
      : this.loadDataFromExperiences(containerId);

    return experiencePromise.then((experience) => {
      const payload = {
        response: experience,
      };

      this.actionsSubject.next(
        new FanreelCustomizationAction(
          FanreelCustomizationActionType.LOAD,
          payload,
        ),
      );

      if (mediaSource) {
        this.actionsSubject.next(
          new FanreelCustomizationAction(
            FanreelCustomizationActionType.UPDATE_CUSTOMIZATION,
            {
              key: 'mediaSource',
              value: mediaSource,
            },
          ),
        );
      }
      if (dataSourceId) {
        this.actionsSubject.next(
          new FanreelCustomizationAction(
            FanreelCustomizationActionType.UPDATE_CUSTOMIZATION,
            {
              key: 'dataSourceId',
              value: dataSourceId,
            },
          ),
        );
      }
    });
  }

  public setPage(newPage: string) {
    const payload = { newPage };

    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.SET_PAGE,
        payload,
      ),
    );
  }

  public nextPage() {
    this.actionsSubject.next(
      new FanreelCustomizationAction(FanreelCustomizationActionType.NEXT_PAGE),
    );
  }

  public previousPage() {
    this.actionsSubject.next(
      new FanreelCustomizationAction(FanreelCustomizationActionType.PREV_PAGE),
    );
  }

  public calculatePageIndex(page: FanreelCustomizationPages) {
    return _(this.PAGE_ORDER).findIndex((p) => p === page);
  }

  public getTotalPages() {
    return this.PAGE_ORDER.length;
  }

  public updateCustomizationOption<T extends FanreelCustomizationValue>(
    key: string,
    value: T,
  ) {
    const payload = {
      key,
      value: value.getValueDTO(),
    };

    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.UPDATE_CUSTOMIZATION,
        payload,
      ),
    );
  }

  public getNextPageFromCurrentState(currentState: any, prev: boolean = false) {
    const currentPage: FanreelCustomizationPages = currentState.currentPage;
    if (currentPage === undefined) {
      return this.STARTING_PAGE;
    }

    const pageIndex = this.calculatePageIndex(currentPage);

    // Calculate the disabled pages
    const disabledPageIndices = [];
    const mediaSource = currentState?.customizationOptions?.mediaSource;
    if (mediaSource && mediaSource.indexOf(Constants.DataSourcePrefix) === 0) {
      disabledPageIndices.push(
        this.calculatePageIndex(FanreelCustomizationPages.UPLOADER),
      );
    }

    // Skip over disabled pages
    const increment = prev ? -1 : 1;
    let targetPage = pageIndex + increment;
    while (disabledPageIndices.indexOf(targetPage) !== -1) {
      targetPage += increment;
    }

    const finalTargetPage = prev
      ? Math.max(targetPage, 0)
      : Math.min(targetPage, this.PAGE_ORDER.length - 1);
    return this.PAGE_ORDER[finalTargetPage];
  }

  public getPageOrder() {
    return this.PAGE_ORDER;
  }

  public clearSnippet() {
    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.CLEAR_SNIPPET,
      ),
    );
  }

  public $onDestroy() {
    this.unsubscribe();
  }

  public showError(state: any) {
    if (Object.keys(state.errorState).length !== 0) {
      if (!this.showValidationStatus) {
        this.customizations.showCustimisationPanelErrorStatus();
      }
    } else {
      if (this.showValidationStatus) {
        this.customizations.hideCustomizationPanelErrorStatus();
      }
    }
  }

  private convertLoadMoreCtaExperienceOptionsToFanreelCustomizations(options) {
    if (options.loadMoreButtonStyle === CtaButtonStyle.TEXT) {
      return {
        loadMoreCtaColor: options.loadMoreTextColor,
        loadMoreCtaShape: CtaButtonStyle.RECTANGLE,
        loadMoreCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset loadMoreTextColor to default when using loadMoreCtaColor for TEXT style option
        loadMoreTextColor: '#FFFFFF',
      };
    } else {
      const loadMoreCtaShape =
        options.loadMoreButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (this.isValueTransparent(options.loadMoreBackgroundColor)) {
        return {
          loadMoreCtaColor: options.loadMoreTextColor,
          loadMoreCtaShape,
          loadMoreCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset loadMoreTextColor to default when using loadMoreCtaColor for OUTLINED style option
          loadMoreTextColor: '#FFFFFF',
        };
      } else {
        return {
          loadMoreCtaColor: options.loadMoreBackgroundColor,
          loadMoreCtaShape,
          loadMoreCtaStyle: FanreelCustomizationCtaType.FILLED,
          loadMoreTextColor: options.loadMoreTextColor,
        };
      }
    }
  }

  private convertHeaderUploaderCtaExperienceOptionsToFanreelCustomizations(
    options,
  ) {
    if (options.headerUploaderCtaButtonStyle === CtaButtonStyle.TEXT) {
      return {
        headerUploaderCtaColor: options.headerUploaderCtaTextColor,
        headerUploaderCtaShape: CtaButtonStyle.RECTANGLE,
        headerUploaderCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset headerUploaderCtaTextColor to default when using headerUploaderCtaColor for TEXT style option
        headerUploaderCtaTextColor: '#FFFFFF',
      };
    } else {
      const headerUploaderCtaShape =
        options.headerUploaderCtaButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (this.isValueTransparent(options.headerUploaderCtaBackgroundColor)) {
        return {
          headerUploaderCtaColor: options.headerUploaderCtaTextColor,
          headerUploaderCtaShape,
          headerUploaderCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset headerUploaderCtaTextColor to default when using headerUploaderCtaColor for OUTLINED style option
          headerUploaderCtaTextColor: '#FFFFFF',
        };
      } else {
        return {
          headerUploaderCtaColor: options.headerUploaderCtaBackgroundColor,
          headerUploaderCtaShape,
          headerUploaderCtaStyle: FanreelCustomizationCtaType.FILLED,
          headerUploaderCtaTextColor: options.headerUploaderCtaTextColor,
        };
      }
    }
  }

  private convertHeaderGalleryLinkCtaExperienceOptionsToFanreelCustomizations(
    options,
  ) {
    if (options.headerGalleryLinkCtaButtonStyle === CtaButtonStyle.TEXT) {
      return {
        headerGalleryLinkCtaColor: options.headerGalleryLinkCtaTextColor,
        headerGalleryLinkCtaShape: CtaButtonStyle.RECTANGLE,
        headerGalleryLinkCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset headerGalleryLinkCtaTextColor to default when using headerGalleryLinkCtaColor for TEXT style option
        headerGalleryLinkCtaTextColor: '#FFFFFF',
      };
    } else {
      const headerGalleryLinkCtaShape =
        options.headerGalleryLinkCtaButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (
        this.isValueTransparent(options.headerGalleryLinkCtaBackgroundColor)
      ) {
        return {
          headerGalleryLinkCtaColor: options.headerGalleryLinkCtaTextColor,
          headerGalleryLinkCtaShape,
          headerGalleryLinkCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset headerGalleryLinkCtaTextColor to default
          // when using headerGalleryLinkCtaColor for OUTLINED style option
          headerGalleryLinkCtaTextColor: '#FFFFFF',
        };
      } else {
        return {
          headerGalleryLinkCtaColor:
            options.headerGalleryLinkCtaBackgroundColor,
          headerGalleryLinkCtaShape,
          headerGalleryLinkCtaStyle: FanreelCustomizationCtaType.FILLED,
          headerGalleryLinkCtaTextColor: options.headerGalleryLinkCtaTextColor,
        };
      }
    }
  }

  private convertModalAddToCartCtaExperienceOptionsToFanreelCustomizations(
    options,
  ) {
    if (options.modalAddToCartButtonStyle === CtaButtonStyle.TEXT) {
      return {
        modalAddToCartCtaColor: options.modalAddToCartTextColor,
        modalAddToCartCtaShape: CtaButtonStyle.RECTANGLE,
        modalAddToCartCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset modalAddToCartTextColor to default when using modalAddToCartCtaColor for TEXT style option
        modalAddToCartTextColor: '#FFFFFF',
      };
    } else {
      const modalAddToCartCtaShape =
        options.modalAddToCartButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (this.isValueTransparent(options.modalAddToCartBackgroundColor)) {
        return {
          modalAddToCartCtaColor: options.modalAddToCartTextColor,
          modalAddToCartCtaShape,
          modalAddToCartCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset modalAddToCartTextColor to default when using modalAddToCartCtaColor for OUTLINED style option
          modalAddToCartTextColor: '#FFFFFF',
        };
      } else {
        return {
          modalAddToCartCtaColor: options.modalAddToCartBackgroundColor,
          modalAddToCartCtaShape,
          modalAddToCartCtaStyle: FanreelCustomizationCtaType.FILLED,
          modalAddToCartTextColor: options.modalAddToCartTextColor,
        };
      }
    }
  }

  private convertModalAddToCartViewMoreCtaExperienceOptionsToFanreelCustomizations(
    options,
  ) {
    if (options.modalAddToCartViewMoreButtonStyle === CtaButtonStyle.TEXT) {
      return {
        modalAddToCartViewMoreCtaColor: options.modalAddToCartViewMoreTextColor,
        modalAddToCartViewMoreCtaShape: CtaButtonStyle.RECTANGLE,
        modalAddToCartViewMoreCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset modalAddToCartViewMoreTextColor to default when using modalAddToCartViewMoreCtaColor for TEXT style option
        modalAddToCartViewMoreTextColor: '#FFFFFF',
      };
    } else {
      const modalAddToCartViewMoreCtaShape =
        options.modalAddToCartViewMoreButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (
        this.isValueTransparent(options.modalAddToCartViewMoreBackgroundColor)
      ) {
        return {
          modalAddToCartViewMoreCtaColor:
            options.modalAddToCartViewMoreTextColor,
          modalAddToCartViewMoreCtaShape,
          modalAddToCartViewMoreCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset modalAddToCartViewMoreTextColor to default when using modalAddToCartViewMoreCtaColor for OUTLINED style option
          modalAddToCartViewMoreTextColor: '#FFFFFF',
        };
      } else {
        return {
          modalAddToCartViewMoreCtaColor:
            options.modalAddToCartViewMoreBackgroundColor,
          modalAddToCartViewMoreCtaShape,
          modalAddToCartViewMoreCtaStyle: FanreelCustomizationCtaType.FILLED,
          modalAddToCartViewMoreTextColor:
            options.modalAddToCartViewMoreTextColor,
        };
      }
    }
  }

  private convertHoverCtaExperienceOptionsToFanreelCustomizations(options) {
    if (options.hoverCtaButtonStyle === CtaButtonStyle.TEXT) {
      return {
        hoverCtaColor: options.hoverCtaTextColor,
        hoverCtaShape: CtaButtonStyle.RECTANGLE,
        hoverCtaStyle: FanreelCustomizationCtaType.TEXT,
        // reset hoverCtaTextColor to default when using hoverCtaColor for TEXT style option
        hoverCtaTextColor: options.hoverTextColor,
      };
    } else {
      const hoverCtaShape =
        options.hoverCtaButtonStyle === CtaButtonStyle.RECTANGLE
          ? CtaButtonStyle.RECTANGLE
          : CtaButtonStyle.PILL;

      // transparent background and not TEXT style means it is an OUTLINED button
      if (this.isValueTransparent(options.hoverCtaBackgroundColor)) {
        return {
          hoverCtaColor: options.hoverCtaTextColor,
          hoverCtaShape,
          hoverCtaStyle: FanreelCustomizationCtaType.OUTLINED,
          // reset hoverCtaTextColor to default when using hoverCtaColor for OUTLINED style option
          hoverCtaTextColor: options.hoverTextColor,
        };
      } else {
        return {
          hoverCtaColor: options.hoverCtaBackgroundColor,
          hoverCtaShape,
          hoverCtaStyle: FanreelCustomizationCtaType.FILLED,
          hoverCtaTextColor: options.hoverCtaTextColor,
        };
      }
    }
  }

  private isValueTransparent(value): boolean {
    return value.toLowerCase() === 'transparent';
  }

  private convertModalAddToCartApiToUIFields(options) {
    const result: any = {};

    if (options.modalAddToCartIsEnabled !== undefined) {
      result.showAddToCart = options.modalAddToCartIsEnabled;
    }

    if (options.modalAddToCartIsEnabled) {
      if (options.modalAddToCartButtonStyle === CtaButtonStyle.TEXT) {
        result.primaryButtonStyle = FanreelCustomizationCtaType.TEXT;
        result.primaryButtonColor = options.modalAddToCartTextColor;
      } else if (
        this.isValueTransparent(options.modalAddToCartBackgroundColor)
      ) {
        result.primaryButtonStyle = FanreelCustomizationCtaType.OUTLINED;
        result.primaryButtonColor = options.modalAddToCartTextColor;
      } else {
        result.primaryButtonStyle = FanreelCustomizationCtaType.FILLED;
        result.primaryButtonColor = options.modalAddToCartBackgroundColor;
        result.primaryButtonTextColor = options.modalAddToCartTextColor;
      }

      result.primaryButtonShape = options.modalAddToCartButtonStyle;
    }

    return result;
  }

  private convertModalAddToCartViewMoreApiToUIFields(options) {
    const result: any = {};

    // Only convert fields if add to cart is enabled
    if (options.modalAddToCartIsEnabled) {
      // Determine button style based on background color and button style
      if (options.modalAddToCartViewMoreButtonStyle === CtaButtonStyle.TEXT) {
        result.secondaryButtonStyle = FanreelCustomizationCtaType.TEXT;
        result.secondaryButtonColor = options.modalAddToCartViewMoreTextColor;
      } else if (
        this.isValueTransparent(options.modalAddToCartViewMoreBackgroundColor)
      ) {
        result.secondaryButtonStyle = FanreelCustomizationCtaType.OUTLINED;
        result.secondaryButtonColor = options.modalAddToCartViewMoreTextColor;
      } else {
        result.secondaryButtonStyle = FanreelCustomizationCtaType.FILLED;
        result.secondaryButtonColor =
          options.modalAddToCartViewMoreBackgroundColor;
        result.secondaryButtonTextColor =
          options.modalAddToCartViewMoreTextColor;
      }

      // Set button shape
      result.secondaryButtonShape = options.modalAddToCartViewMoreButtonStyle;
    }

    return result;
  }

  private scanLoop(state: any, action: FanreelCustomizationAction) {
    let newState = angular.copy(state);

    switch (action.typ) {
      case FanreelCustomizationActionType.LOAD:
        this.initialObject = null;
        newState = this.getStateFromResponse(action.payload.response);
        newState.loaded = true;
        newState.currentPage = this.STARTING_PAGE;
        newState.customizationOptions.locale = Locales.EN_US;
        if (newState.customizationOptions.localizationStrings) {
          // load default english locale strings in the keys
          newState.customizationOptions.headerText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].headerText;
          newState.customizationOptions.headerSubtext =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].headerSubtext;
          newState.customizationOptions.galleryLinkCta =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].galleryLinkCta;
          newState.customizationOptions.galleryLinkUrl =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].galleryLinkUrl;
          newState.customizationOptions.hoverText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].hoverText;
          newState.customizationOptions.ecommerceProductDetailsCta =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].ecommerceProductDetailsCta;
          newState.customizationOptions.itemTrayTitle =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].itemTrayTitle;
          // Ensure modal button texts are populated with en-US values on initial load
          const currentLocaleStrings =
            newState.customizationOptions.localizationStrings?.[Locales.EN_US];
          const defaultLocaleStrings =
            DefaultLocaleStrings.localizationStrings[Locales.EN_US];

          newState.customizationOptions.primaryButtonText =
            currentLocaleStrings?.primaryButtonText ??
            defaultLocaleStrings.primaryButtonText;
          newState.customizationOptions.secondaryButtonText =
            currentLocaleStrings?.secondaryButtonText ??
            defaultLocaleStrings.secondaryButtonText;
          /*
            add the default EN-US load more cta text for Load More button since there
            is no option to customize it in the first 3 screens
          */
          newState.customizationOptions.loadMoreCtaText =
            newState.customizationOptions.localizationStrings[Locales.EN_US]
              .loadMoreCtaText ||
            DefaultLocaleStrings.localizationStrings[Locales.EN_US]
              ?.loadMoreCtaText;
          // If uploaderCtaText is empty, populate with default and update localizationStrings
          if (
            !newState.customizationOptions.localizationStrings[Locales.EN_US]
              .uploaderCtaText
          ) {
            const defaultUploaderCtaText =
              DefaultLocaleStrings.localizationStrings[Locales.EN_US]
                ?.uploaderCtaText;
            newState.customizationOptions.uploaderCtaText =
              defaultUploaderCtaText;
            // Create a new copy of both localizationStrings and the locale object to avoid mutating frozen objects
            newState.customizationOptions.localizationStrings = {
              ...newState.customizationOptions.localizationStrings,
              [Locales.EN_US]: {
                ...newState.customizationOptions.localizationStrings[
                  Locales.EN_US
                ],
                uploaderCtaText: defaultUploaderCtaText,
              },
            };
          } else {
            newState.customizationOptions.uploaderCtaText =
              newState.customizationOptions.localizationStrings[
                Locales.EN_US
              ].uploaderCtaText;
          }
          newState.customizationOptions.uploaderTermsAndConditionsLink =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].uploaderTermsAndConditionsLink;
        } else {
          //handle scenario when a newly created gallery does not have localizationStrings object
          newState.customizationOptions.localizationStrings = {};
          newState.customizationOptions.localizationStrings[Locales.EN_US] = {};
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].headerText = newState.customizationOptions.headerText || '';
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].headerSubtext = newState.customizationOptions.headerSubtext || '';
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].galleryLinkCta = newState.customizationOptions.galleryLinkCta || '';
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].galleryLinkUrl = newState.customizationOptions.galleryLinkUrl || '';
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].hoverText = newState.customizationOptions.hoverText || '';
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].itemTrayTitle = newState.customizationOptions.itemTrayTitle || '';
          /*
            Load the default EN-US load more cta text since for the newly created
            gallery it might not be available yet
          */
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].loadMoreCtaText =
            newState.customizationOptions.loadMoreCtaText ||
            DefaultLocaleStrings.localizationStrings?.[Locales.EN_US]
              ?.loadMoreCtaText;
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].ecommerceProductDetailsCta =
            newState.customizationOptions.ecommerceProductDetailsCta ||
            DefaultLocaleStrings.localizationStrings[Locales.EN_US]
              .ecommerceProductDetailsCta;
          // Initialize modal button text fields for default English locale
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].primaryButtonText =
            newState.customizationOptions.primaryButtonText ||
            DefaultLocaleStrings.localizationStrings[Locales.EN_US]
              .primaryButtonText;
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].secondaryButtonText =
            newState.customizationOptions.secondaryButtonText ||
            DefaultLocaleStrings.localizationStrings[Locales.EN_US]
              ?.secondaryButtonText;
          // If uploaderCtaText is empty, populate with default in both places
          const uploaderDefaultText =
            DefaultLocaleStrings.localizationStrings[Locales.EN_US]
              ?.uploaderCtaText;
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].uploaderCtaText =
            newState.customizationOptions.uploaderCtaText ||
            uploaderDefaultText;
          newState.customizationOptions.localizationStrings[
            Locales.EN_US
          ].uploaderTermsAndConditionsLink =
            newState.customizationOptions.uploaderTermsAndConditionsLink || '';
        }
        break;
      case FanreelCustomizationActionType.DEPLOY:
        this.deployFromState(
          newState,
          action.payload.containerId,
          action.payload.containerType,
        );
        break;
      case FanreelCustomizationActionType.DEPLOYMENT_COMPLETE:
        newState.deploymentComplete = true;
        newState.hasUnsavedChanges = false;
        break;
      case FanreelCustomizationActionType.GENERATE_PREVIEW:
        this.generatePreviewFromOptions(
          action.payload.customizationOptions,
          action.payload.overrideCustomizations,
          action.payload.containerId,
          action.payload.customizationOptions.locale,
        );
        break;
      case FanreelCustomizationActionType.NEXT_PAGE:
        newState.currentPage = this.getNextPageFromCurrentState(state);
        break;
      case FanreelCustomizationActionType.PREV_PAGE:
        newState.currentPage = this.getNextPageFromCurrentState(state, true);
        break;
      case FanreelCustomizationActionType.SET_PREVIEW_SNIPPET:
        newState.previewSnippet = action.payload.snippet;
        break;
      case FanreelCustomizationActionType.CLEAR_SNIPPET:
        newState.previewSnippet = undefined;
        break;
      case FanreelCustomizationActionType.SET_PAGE:
        newState.currentPage = action.payload.newPage;
        if (newState.currentPage !== FanreelCustomizationPages.LOCALIZE) {
          /*
            Handling scenarios where a client has not enabled Localization feature,
            Load more cta text will be empty and there won't be any way to customize it.
            Hence adding the default EN-US load more cta text for the Load More button
          */
          if (
            !newState.customizationOptions.localizationStrings[Locales.EN_US]
              .loadMoreCtaText
          ) {
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].loadMoreCtaText =
              DefaultLocaleStrings.localizationStrings[
                Locales.EN_US
              ].loadMoreCtaText;
          }
          // load default english locale strings in the keys
          newState.customizationOptions.locale = Locales.EN_US;
          newState.customizationOptions.headerText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].headerText;
          newState.customizationOptions.headerSubtext =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].headerSubtext;
          newState.customizationOptions.galleryLinkCta =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].galleryLinkCta;
          newState.customizationOptions.galleryLinkUrl =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].galleryLinkUrl;
          newState.customizationOptions.hoverText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].hoverText;
          newState.customizationOptions.ecommerceProductDetailsCta =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].ecommerceProductDetailsCta;
          newState.customizationOptions.itemTrayTitle =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].itemTrayTitle;
          // Ensure Add to Cart button texts revert to en-US when returning from Localization
          if (newState.customizationOptions.showAddToCart) {
            const { customizationOptions } = newState;

            if (customizationOptions.showAddToCart) {
              const currentLocaleStrings =
                customizationOptions.localizationStrings?.[Locales.EN_US];
              const defaultLocaleStrings =
                DefaultLocaleStrings.localizationStrings[Locales.EN_US];

              customizationOptions.primaryButtonText =
                currentLocaleStrings?.primaryButtonText ??
                defaultLocaleStrings.primaryButtonText;

              customizationOptions.secondaryButtonText =
                currentLocaleStrings?.secondaryButtonText ??
                defaultLocaleStrings.secondaryButtonText;
            }
          }
          newState.customizationOptions.loadMoreCtaText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].loadMoreCtaText;
          newState.customizationOptions.uploaderCtaText =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].uploaderCtaText;
          newState.customizationOptions.uploaderTermsAndConditionsLink =
            newState.customizationOptions.localizationStrings[
              Locales.EN_US
            ].uploaderTermsAndConditionsLink;
        }
        break;
      case FanreelCustomizationActionType.START_ASYNC:
        if (!newState.outstandingAsyncActions) {
          newState.outstandingAsyncActions = [];
        }
        newState.outstandingAsyncActions.push(action.payload.actionType);
        break;
      case FanreelCustomizationActionType.STOP_ASYNC:
        newState.outstandingAsyncActions = _(
          newState.outstandingAsyncActions,
        ).without(action.payload.actionType);
        break;
      case FanreelCustomizationActionType.UPDATE_CUSTOMIZATION:
        if (!newState.customizationOptions) {
          newState.customizationOptions = {};
        }
        newState.customizationOptions[action.payload.key] =
          action.payload.value;

        //add the updated localized strings to localizationStrings object for that locale
        if (
          DefaultLocaleStrings.localizationStrings[
            newState.customizationOptions.locale
          ].hasOwnProperty(action.payload.key)
        ) {
          if (
            !newState.customizationOptions.localizationStrings[
              newState.customizationOptions.locale
            ]
          ) {
            newState.customizationOptions.localizationStrings[
              newState.customizationOptions.locale
            ] = {};
          }
          newState.customizationOptions.localizationStrings[
            newState.customizationOptions.locale
          ][action.payload.key] = action.payload.value;
        }

        // localization dropdown changes
        if (action.payload.key === 'locale') {
          //if the selected locale is not present in the localizationStrings object
          if (
            !newState.customizationOptions.localizationStrings[
              action.payload.value
            ]
          ) {
            // put the default locale strings in the localizationStrings Object for that locale
            newState.customizationOptions.localizationStrings[
              action.payload.value
            ] = {
              headerText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .headerText,
              headerSubtext:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .headerSubtext,
              galleryLinkUrl:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .headerSubtext,
              galleryLinkCta:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .galleryLinkCta,
              hoverText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .hoverText,
              ecommerceProductDetailsCta:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .ecommerceProductDetailsCta,
              itemTrayTitle:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .itemTrayTitle,
              loadMoreCtaText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .loadMoreCtaText,
              uploaderCtaText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .uploaderCtaText,
              uploaderTermsAndConditionsLink:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .uploaderTermsAndConditionsLink,
              primaryButtonText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .primaryButtonText,
              secondaryButtonText:
                DefaultLocaleStrings.localizationStrings[action.payload.value]
                  .secondaryButtonText,
            };
            // add those default locale strings in the locale keys
            newState.customizationOptions.headerText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].headerText;
            newState.customizationOptions.headerSubtext =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].headerSubtext;
            newState.customizationOptions.galleryLinkUrl =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].galleryLinkUrl;
            newState.customizationOptions.galleryLinkCta =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].galleryLinkCta;
            newState.customizationOptions.hoverText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].hoverText;
            newState.customizationOptions.ecommerceProductDetailsCta =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].ecommerceProductDetailsCta;
            newState.customizationOptions.itemTrayTitle =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].itemTrayTitle;
            newState.customizationOptions.loadMoreCtaText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ]?.loadMoreCtaText;
            newState.customizationOptions.uploaderCtaText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ]?.uploaderCtaText;
            newState.customizationOptions.uploaderTermsAndConditionsLink =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].uploaderTermsAndConditionsLink;

            newState.customizationOptions.primaryButtonText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].primaryButtonText;
            newState.customizationOptions.secondaryButtonText =
              DefaultLocaleStrings.localizationStrings[
                action.payload.value
              ].secondaryButtonText;
          } else {
            // set the locale keys to be the value of those keys in the localizationStrings object for that locale
            newState.customizationOptions.headerText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].headerText;
            newState.customizationOptions.headerSubtext =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].headerSubtext;
            newState.customizationOptions.galleryLinkCta =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].galleryLinkCta;
            newState.customizationOptions.galleryLinkUrl =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].galleryLinkUrl;
            newState.customizationOptions.hoverText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].hoverText;
            newState.customizationOptions.ecommerceProductDetailsCta =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].ecommerceProductDetailsCta;
            newState.customizationOptions.itemTrayTitle =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].itemTrayTitle;
            newState.customizationOptions.loadMoreCtaText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].loadMoreCtaText;
            newState.customizationOptions.uploaderCtaText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].uploaderCtaText;
            newState.customizationOptions.uploaderTermsAndConditionsLink =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].uploaderTermsAndConditionsLink;

            newState.customizationOptions.primaryButtonText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].primaryButtonText;
            newState.customizationOptions.secondaryButtonText =
              newState.customizationOptions.localizationStrings[
                action.payload.value
              ].secondaryButtonText;

            const targetLocale = action.payload.value;
            const localeStrings =
              newState.customizationOptions.localizationStrings[targetLocale];

            if (localeStrings && !localeStrings.primaryButtonText) {
              localeStrings.primaryButtonText =
                DefaultLocaleStrings.localizationStrings[
                  targetLocale
                ].primaryButtonText;
              localeStrings.secondaryButtonText =
                DefaultLocaleStrings.localizationStrings[
                  targetLocale
                ].secondaryButtonText;
            }

            if (localeStrings) {
              newState.customizationOptions.primaryButtonText =
                localeStrings.primaryButtonText;
              newState.customizationOptions.secondaryButtonText =
                localeStrings.secondaryButtonText;
            }
          }
        }
        break;
      case FanreelCustomizationActionType.UPDATE_HAS_UNSAVED_CHANGES:
        newState.hasUnsavedChanges = action.payload;
        break;
      default:
        break;
    }

    newState.showCarouselNavigationButtonStyle =
      this.getIsCarouselNavigationButtonStyleEnabled(newState);
    newState.showHeaderGalleryLinkCtaTextColorOption =
      this.getIsCtaTextColorOptionEnabled(
        newState.customizationOptions?.headerGalleryLinkCtaStyle,
      );
    newState.showHeaderUploaderCtaTextColorOption =
      this.getIsCtaTextColorOptionEnabled(
        newState.customizationOptions?.headerUploaderCtaStyle,
      );
    newState.showHoverCtaTextColorOption = this.getIsCtaTextColorOptionEnabled(
      newState.customizationOptions?.hoverCtaStyle,
    );
    newState.showLoadMoreCustomizationOptions =
      this.getIsLoadMoreOptionsEnabled(newState);
    newState.showLoadMoreTextColorOption = this.getIsCtaTextColorOptionEnabled(
      newState.customizationOptions?.loadMoreCtaStyle,
    );
    newState.showPrimaryButtonTextColorOption =
      this.getIsCtaTextColorOptionEnabled(
        newState.customizationOptions?.primaryButtonStyle,
      );
    newState.showSecondaryButtonTextColorOption =
      this.getIsCtaTextColorOptionEnabled(
        newState.customizationOptions?.secondaryButtonStyle,
      );
    newState.errorState = this.calculateErrorState(newState);
    newState.locales = this.locales || ['en-us']; // add the locales from sites api to the state
    return newState;
  }

  private startAsyncAction(action: FanreelCustomizationActionType) {
    const payload = {
      actionType: action,
    };
    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.START_ASYNC,
        payload,
      ),
    );
  }

  private stopAsyncAction(action: FanreelCustomizationActionType) {
    const payload = {
      actionType: action,
    };

    this.actionsSubject.next(
      new FanreelCustomizationAction(
        FanreelCustomizationActionType.STOP_ASYNC,
        payload,
      ),
    );
  }

  private getStateFromResponse(response: any) {
    let customizationOptions: any = {};

    if (response) {
      customizationOptions.layout = response.experienceLayout;
      customizationOptions.mediaSource = response.experienceDataSource;

      const options = response.experienceCustomizationOptions;

      if (options) {
        customizationOptions = _(customizationOptions).extend(options);

        if (options.font) {
          customizationOptions.font = options.font;
        }

        if (options.subheaderFontSize) {
          customizationOptions.subheaderFontSize =
            options.subheaderFontSize.toString();
        }

        if (options.headerFontSize) {
          customizationOptions.headerFontSize =
            options.headerFontSize.toString();
        }

        if (options.numberOfRows) {
          customizationOptions.numberOfRows = options.numberOfRows.toString();
        }

        if (options.numberOfColumns) {
          customizationOptions.numberOfColumns =
            options.numberOfColumns.toString();
        }

        if (options.minNumberOfColumns) {
          customizationOptions.minNumberOfColumns =
            options.minNumberOfColumns.toString();
        }

        if (options.loadMoreBackgroundColor) {
          customizationOptions.loadMoreBackgroundColor =
            options.loadMoreBackgroundColor.toString();
        }

        if (options.loadMoreTextColor) {
          customizationOptions.loadMoreTextColor =
            options.loadMoreTextColor.toString();
        }

        if (options.hoverOverlayColor && options.hoverTextColor) {
          customizationOptions.hoverStateColor = this.getThemeForColors(
            options.hoverOverlayColor,
            options.hoverTextColor,
          );
        }

        if (
          isDefined(options.modalHeroIsSquare) &&
          isNotNull(options.modalHeroIsSquare)
        ) {
          if (options.modalHeroIsSquare) {
            customizationOptions.modalImageAspectRatio =
              FanreelCustomizationImageAspectRatios.SQUARE;
          } else {
            customizationOptions.modalImageAspectRatio =
              FanreelCustomizationImageAspectRatios.ORIGINAL;
          }
        }

        if (
          isDefined(options.galleryThumbnailImageIsSquare) &&
          isNotNull(options.galleryThumbnailImageIsSquare)
        ) {
          if (options.galleryThumbnailImageIsSquare) {
            customizationOptions.galleryThumbnailImageAspectRatio =
              FanreelCustomizationImageAspectRatios.SQUARE;
          } else {
            customizationOptions.galleryThumbnailImageAspectRatio =
              FanreelCustomizationImageAspectRatios.ORIGINAL;
          }
        }

        if (
          isDefined(options.carouselThumbnailImageIsSquare) &&
          isNotNull(options.carouselThumbnailImageIsSquare)
        ) {
          if (options.carouselThumbnailImageIsSquare) {
            customizationOptions.carouselThumbnailImageAspectRatio =
              FanreelCustomizationImageAspectRatios.SQUARE;
          } else {
            customizationOptions.carouselThumbnailImageAspectRatio =
              FanreelCustomizationImageAspectRatios.ORIGINAL;
          }
        }

        // If carouselNavigationIconStyle is not defined/set to transparent set carouselNavigationArrowStyle to "Arrow"
        if (
          !options.carouselNavigationIconStyle ||
          options.carouselNavigationIconStyle === IconStyle.TRANSPARENT
        ) {
          customizationOptions.carouselNavigationArrowStyle =
            FanreelCustomizationCarouselNavigationArrowStyle.ARROW;
        } else {
          // If carouselNavigationIconStyle is not transparent, we need to set the arrowStyle to be "Arrow with Button"
          // to enable the Button Shape and Colors options. carouselNavigationButtonStyle can then also be set
          // to carouselNavigationIconStyle since transparent is not a valid value for carouselNavigationButtonStyle
          customizationOptions.carouselNavigationArrowStyle =
            FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON;
          customizationOptions.carouselNavigationButtonStyle =
            options.carouselNavigationIconStyle;
        }

        if (options.loadMoreButtonStyle) {
          const loadMoreCustomizationOptions =
            this.convertLoadMoreCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...loadMoreCustomizationOptions,
          };
        }

        if (options.headerUploaderCtaButtonStyle) {
          const headerUploaderCustomizationOptions =
            this.convertHeaderUploaderCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...headerUploaderCustomizationOptions,
          };
        }

        if (options.headerGalleryLinkCtaButtonStyle) {
          const headerGalleryLinkCustomizationOptions =
            this.convertHeaderGalleryLinkCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...headerGalleryLinkCustomizationOptions,
          };
        }

        if (options.hoverCtaButtonStyle) {
          const hoverCtaCustomizationOptions =
            this.convertHoverCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...hoverCtaCustomizationOptions,
          };
        }

        if (options.modalAddToCartButtonStyle) {
          const modalAddToCartCustomizationOptions =
            this.convertModalAddToCartCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...modalAddToCartCustomizationOptions,
          };

          const modalAddToCartUIOptions =
            this.convertModalAddToCartApiToUIFields(options);
          customizationOptions = {
            ...customizationOptions,
            ...modalAddToCartUIOptions,
          };
        }

        if (options.modalAddToCartViewMoreButtonStyle) {
          const modalAddToCartViewMoreCustomizationOptions =
            this.convertModalAddToCartViewMoreCtaExperienceOptionsToFanreelCustomizations(
              options,
            );
          customizationOptions = {
            ...customizationOptions,
            ...modalAddToCartViewMoreCustomizationOptions,
          };

          const modalAddToCartViewMoreUIOptions =
            this.convertModalAddToCartViewMoreApiToUIFields(options);
          customizationOptions = {
            ...customizationOptions,
            ...modalAddToCartViewMoreUIOptions,
          };
        }

        // Add platform type mapping for round-trip persistence
        if (options.platformType) {
          customizationOptions.ecommercePlatformType = options.platformType;
        }

        if (options.salesforceSiteId !== undefined) {
          customizationOptions.salesforceSiteId = options.salesforceSiteId;
        }
        if (options.modalAddToCartIsEnabled !== undefined) {
          customizationOptions.showAddToCart = options.modalAddToCartIsEnabled;
        }
      }
    }

    return { customizationOptions };
  }

  private getIsLoadMoreOptionsEnabled(state) {
    const layoutsWithLoadMoreCtaOptions = [
      ExperienceLayoutType.MASONRY,
      ExperienceLayoutType.MULTISIZE_GRID,
      ExperienceLayoutType.SQUARE_GRID,
    ];
    const layoutType = state.customizationOptions.layout;
    const paginationType = state.customizationOptions.pagination;

    const isMatchingLayout = _(layoutsWithLoadMoreCtaOptions).contains(
      layoutType,
    );

    // Don't show LoadMore Cta options if infiniteScroll is enabled
    const hasInfiniteScrollEnabled =
      paginationType === Pagination.InfiniteScroll;

    return isMatchingLayout && !hasInfiniteScrollEnabled;
  }

  // The CTA Text Color option is only enabled for FILLED buttons
  // For OUTLINED and TEXT button styles only the CTA Button Color is used
  private getIsCtaTextColorOptionEnabled(
    ctaStyle?: FanreelCustomizationCtaType,
  ) {
    return ctaStyle === FanreelCustomizationCtaType.FILLED;
  }

  private getIsCarouselNavigationButtonStyleEnabled(state) {
    return (
      state.customizationOptions.carouselNavigationArrowStyle ===
      FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON
    );
  }

  private findLayoutPanelErrors(state) {
    const errorMessages = [];

    if (state.customizationOptions.showTitle) {
      if (state.customizationOptions.headerTextColor === undefined) {
        errorMessages.push('Please specify a valid title text color');
      }

      if (
        state.customizationOptions.showSubtitle &&
        state.customizationOptions.subheaderTextColor === undefined
      ) {
        errorMessages.push('Please specify a valid subtitle text color');
      }
    }

    if (!state.customizationOptions.font) {
      errorMessages.push('Please specify a valid Custom Font');
    }

    if (
      state.customizationOptions.layout === ExperienceLayoutType.CAROUSEL ||
      state.customizationOptions.layout === ExperienceLayoutType.VIDEO_COMMERCE
    ) {
      if (!state.customizationOptions.carouselNavigationIconColor) {
        errorMessages.push('Please specify a valid Carousel Arrow icon color');
      }

      if (
        state.customizationOptions.carouselNavigationArrowStyle ===
          FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON &&
        !state.customizationOptions.carouselNavigationBackgroundColor
      ) {
        errorMessages.push(
          'Please specify a valid Carousel Navigation button color',
        );
      }

      if (
        state.customizationOptions.carouselNavigationArrowStyle ===
          FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON &&
        !state.customizationOptions.carouselNavigationBorderColor
      ) {
        errorMessages.push(
          'Please specify a valid Carousel Navigation button border color',
        );
      }
    }

    if (state.showLoadMoreCustomizationOptions) {
      if (!state.customizationOptions.loadMoreCtaColor) {
        errorMessages.push('Please specify a valid Load More button color');
      }

      if (
        state.showLoadMoreTextColorOption &&
        !state.customizationOptions.loadMoreTextColor
      ) {
        errorMessages.push('Please specify a valid Load More text color');
      }
    }

    if (state.customizationOptions.showGalleryLink) {
      if (!state.customizationOptions.headerGalleryLinkCtaColor) {
        errorMessages.push('Please specify a valid Gallery Link button color');
      }

      if (
        state.showHeaderGalleryLinkCtaTextColorOption &&
        !state.customizationOptions.headerGalleryLinkCtaTextColor
      ) {
        errorMessages.push('Please specify a valid Gallery Link text color');
      }

      if (!state.customizationOptions.galleryLinkUrl) {
        errorMessages.push('Please specify a valid Gallery Link URL');
      }
    }

    if (
      state.customizationOptions.carouselHoverShowCta ||
      state.customizationOptions.galleryHoverShowCta
    ) {
      if (!state.customizationOptions.hoverCtaColor) {
        errorMessages.push('Please specify a valid Hover button color');
      }

      if (
        state.showHoverCtaTextColorOption &&
        !state.customizationOptions.hoverCtaTextColor
      ) {
        errorMessages.push('Please specify a valid Hover button text color');
      }
    }

    return errorMessages;
  }

  private findCustomizationPanelErrors(state) {
    const errorMessages = [];

    if (
      state.customizationOptions.layout !==
        ExperienceLayoutType.VIDEO_COMMERCE &&
      state.customizationOptions.modalTextColor === undefined
    ) {
      errorMessages.push('Please specify a valid text color');
    }

    if (
      state.customizationOptions.layout ===
        ExperienceLayoutType.VIDEO_COMMERCE &&
      state.customizationOptions.modalBackgroundColor === undefined
    ) {
      errorMessages.push('Please specify a valid background color');
    }

    if (
      !this.sessionVarsService.isFeatureEnabled('fanreel_self_serve_modal_v2')
    ) {
      if (state.customizationOptions.ctaButtonColor === undefined) {
        errorMessages.push('Please specify a valid button color');
      }

      if (state.customizationOptions.ctaTextColor === undefined) {
        errorMessages.push('Please specify a valid button text color');
      }
    }

    if (
      state.customizationOptions.layout !==
        ExperienceLayoutType.VIDEO_COMMERCE &&
      state.customizationOptions.iconColor === undefined
    ) {
      errorMessages.push('Please specify a valid icon color');
    }

    const { showAddToCart, ecommercePlatformType, salesforceSiteId } =
      state.customizationOptions;

    if (
      showAddToCart &&
      ecommercePlatformType === PlatformType.SFCC_CONNECTOR
    ) {
      if (!salesforceSiteId)
        errorMessages.push('Please specify a Salesforce Site ID');
    }

    return errorMessages;
  }

  private findUploaderPanelErrors(state) {
    const errorMessages = [];

    if (state.customizationOptions.enableUploader) {
      if (!state.customizationOptions.uploaderTermsAndConditionsLink) {
        errorMessages.push(
          'Please include a link to your Uploader terms & conditions',
        );
      }

      if (!state.customizationOptions.headerUploaderCtaColor) {
        errorMessages.push('Please specify a valid Uploader button color');
      }

      if (
        state.showHeaderUploaderCtaTextColorOption &&
        !state.customizationOptions.headerUploaderCtaTextColor
      ) {
        errorMessages.push('Please specify a valid Uploader text color');
      }
    }

    return errorMessages;
  }

  private findLocalizationPanelErrors(state) {
    let errorMessages: Array<string> = [];
    const errorMessage = ['Please fill out the required fields'];

    if (state.customizationOptions.showTitle) {
      if (!state.customizationOptions.headerText) {
        return (errorMessages = errorMessage);
      }
    }

    if (state.customizationOptions.showSubtitle) {
      if (!state.customizationOptions.headerSubtext) {
        return (errorMessages = errorMessage);
      }
    }

    if (state.customizationOptions.showGalleryLink) {
      if (!state.customizationOptions.galleryLinkCta) {
        return (errorMessages = errorMessage);
      }
      if (!state.customizationOptions.galleryLinkUrl) {
        return (errorMessages = errorMessage);
      }
    }

    // Only validate hover text if hover CTA is enabled and layout is not VIDEO_COMMERCE
    if (
      (state.customizationOptions.carouselHoverShowCta ||
        state.customizationOptions.galleryHoverShowCta) &&
      state.customizationOptions.layout !== ExperienceLayoutType.VIDEO_COMMERCE
    ) {
      if (!state.customizationOptions.hoverText) {
        return (errorMessages = errorMessage);
      }
    }

    // Load more button appears when layout is not carousel and not video commerce
    if (
      state.customizationOptions.layout !== ExperienceLayoutType.CAROUSEL &&
      state.customizationOptions.layout !== ExperienceLayoutType.VIDEO_COMMERCE
    ) {
      if (!state.customizationOptions.loadMoreCtaText) {
        return (errorMessages = errorMessage);
      }
    }

    if (!state.customizationOptions.itemTrayTitle) {
      return (errorMessages = errorMessage);
    }

    if (state.customizationOptions.enableUploader) {
      if (!state.customizationOptions.uploaderCtaText) {
        return (errorMessages = errorMessage);
      }
      if (!state.customizationOptions.uploaderTermsAndConditionsLink) {
        return (errorMessages = errorMessage);
      }
    }

    return errorMessages;
  }

  private calculateErrorState(state: any) {
    const layoutErrors = this.findLayoutPanelErrors(state);

    if (!_(layoutErrors).isEmpty()) {
      return {
        errorPanel: this.calculatePageIndex(FanreelCustomizationPages.LAYOUT),
        errorMessages: layoutErrors,
      };
    }

    const customizationErrors = this.findCustomizationPanelErrors(state);

    if (!_(customizationErrors).isEmpty()) {
      return {
        errorPanel: this.calculatePageIndex(
          FanreelCustomizationPages.CUSTOMIZE,
        ),
        errorMessages: customizationErrors,
      };
    }

    const uploaderErrors = this.findUploaderPanelErrors(state);

    if (!_(uploaderErrors).isEmpty()) {
      return {
        errorPanel: this.calculatePageIndex(FanreelCustomizationPages.UPLOADER),
        errorMessages: uploaderErrors,
      };
    }

    if (this.isLocalizationEnabled) {
      const localizationErrors = this.findLocalizationPanelErrors(state);

      if (!_(localizationErrors).isEmpty()) {
        return {
          errorPanel: this.calculatePageIndex(
            FanreelCustomizationPages.LOCALIZE,
          ),
          errorMessages: localizationErrors,
        };
      }
    }

    return {};
  }

  private deployFromState(
    currentState,
    containerId: ExperienceContainerId,
    containerType: ExperienceContainerType,
  ) {
    const updateRequest = this.computeUpdateRequestFromState(
      currentState.customizationOptions,
      containerId,
      containerType,
    );
    this.experiencesService
      .updateExperienceAsync(containerId, updateRequest)
      .then(() => {
        this.actionsSubject.next(
          new FanreelCustomizationAction(
            FanreelCustomizationActionType.DEPLOYMENT_COMPLETE,
          ),
        );
      })
      .catch((error) => {
        this.$log.error(
          `Unable to deploy fanreel self-serve integration, ${error}`,
        );
        this.errorNotificationService.showDefaultErrorDialog(
          'We were unable to deploy your Fanreel. Please contact Bazaarvoice support for assistance.',
        );
      })
      .finally(() => {
        this.stopAsyncAction(FanreelCustomizationActionType.DEPLOY);
      });
  }

  // Take a list of options; use only sort, filter label set, and media source
  // For everything else use basic square-grid defaults
  private computeDataSourceRequestFromState(options) {
    return {
      customizationOptions: {
        font: null,
        hoverStateColor: 'Dark',
        modalTextColor: '#7E7E7E',
        iconColor: HoverThemeColors.BLACK,
        ctaButtonColor: HoverThemeColors.BLACK,
        ctaTextColor: HoverThemeColors.WHITE,
        headerTextColor: null,
        headerText: null,
        headerSubtext: null,
        galleryLinkCta: null,
        galleryLinkUrl: null,
        showGalleryLink: false,
        showHeader: false,
        headerFontSize: null,
        includeEcommerceMetadata: false,
        modalType: 'Modal',
        itemTrayTitle: null,
        ctaText: 'Shop Now',
        hoverText: null,
        sort: options.sort,
        filterLabelSet: options.filterLabelSet,
        numberOfRows: '4',
        numberOfColumns: '4',
        minNumberOfColumns: '1',
      },
      layout: ExperienceLayoutType.SQUARE_GRID,
      source: options.mediaSource,
    };
  }

  // Use this helper to default border and background colors when carouselNavigationArrowStyle is set to "Arrow"
  private getCarouselNavigationStyleOptions(options) {
    const isNavArrowWithButton =
      options.carouselNavigationArrowStyle ===
      FanreelCustomizationCarouselNavigationArrowStyle.ARROW_WITH_BUTTON;

    return {
      carouselNavigationBackgroundColor: isNavArrowWithButton
        ? options.carouselNavigationBackgroundColor
        : '#FFFFFF',
      carouselNavigationBorderColor: isNavArrowWithButton
        ? options.carouselNavigationBorderColor
        : '#000000',
      carouselNavigationIconStyle: isNavArrowWithButton
        ? options.carouselNavigationButtonStyle
        : IconStyle.TRANSPARENT,
    };
  }

  // Given a theme, return the appropriate overlay and text hex values.
  private getColorsForTheme(
    theme: HoverStateTheme,
    overlay: string,
    text: string,
  ) {
    switch (theme) {
      case HoverStateTheme.DARK:
        return {
          overlay: HoverThemeColors.BLACK,
          text: HoverThemeColors.WHITE,
        };
      case HoverStateTheme.LIGHT:
        return {
          overlay: HoverThemeColors.WHITE,
          text: HoverThemeColors.BLACK,
        };
      case HoverStateTheme.CUSTOM:
        return {
          overlay,
          text,
        };
      default:
        break;
    }
  }

  private getLoadMoreStyleOptions(options) {
    switch (options.loadMoreCtaStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // loadMoreTextColor field only used for filled buttons
        return {
          loadMoreBackgroundColor: options.loadMoreCtaColor,
          loadMoreBorderColor: options.loadMoreCtaColor,
          loadMoreButtonStyle: options.loadMoreCtaShape,
          loadMoreTextColor: options.loadMoreTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use loadMoreCtaColor for text and border colors
        return {
          loadMoreBackgroundColor: 'transparent',
          loadMoreBorderColor: options.loadMoreCtaColor,
          loadMoreButtonStyle: options.loadMoreCtaShape,
          loadMoreTextColor: options.loadMoreCtaColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        // Text Only buttons use border color for underline
        return {
          loadMoreBackgroundColor: 'transparent',
          loadMoreBorderColor: options.loadMoreCtaColor,
          loadMoreButtonStyle: CtaButtonStyle.TEXT,
          loadMoreTextColor: options.loadMoreCtaColor,
        };
      default:
        return {};
    }
  }

  private getHeaderUploaderCtaStyleOptions(options) {
    switch (options.headerUploaderCtaStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // headerUploaderCtaTextColor field only used for filled buttons
        return {
          headerUploaderCtaBackgroundColor: options.headerUploaderCtaColor,
          headerUploaderCtaBorderColor: options.headerUploaderCtaColor,
          headerUploaderCtaButtonStyle: options.headerUploaderCtaShape,
          headerUploaderCtaTextColor: options.headerUploaderCtaTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use headerUploaderCtaColor for text and border colors
        return {
          headerUploaderCtaBackgroundColor: 'transparent',
          headerUploaderCtaBorderColor: options.headerUploaderCtaColor,
          headerUploaderCtaButtonStyle: options.headerUploaderCtaShape,
          headerUploaderCtaTextColor: options.headerUploaderCtaColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        return {
          headerUploaderCtaBackgroundColor: 'transparent',
          headerUploaderCtaBorderColor: options.headerUploaderCtaColor,
          headerUploaderCtaButtonStyle: CtaButtonStyle.TEXT,
          headerUploaderCtaTextColor: options.headerUploaderCtaColor,
        };
      default:
        return {};
    }
  }

  private getHeaderGalleryLinkCtaStyleOptions(options) {
    switch (options.headerGalleryLinkCtaStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // headerGalleryLinkCtaTextColor field only used for filled buttons
        return {
          headerGalleryLinkCtaBackgroundColor:
            options.headerGalleryLinkCtaColor,
          headerGalleryLinkCtaBorderColor: options.headerGalleryLinkCtaColor,
          headerGalleryLinkCtaButtonStyle: options.headerGalleryLinkCtaShape,
          headerGalleryLinkCtaTextColor: options.headerGalleryLinkCtaTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use headerGalleryLinkCtaColor for text and border colors
        return {
          headerGalleryLinkCtaBackgroundColor: 'transparent',
          headerGalleryLinkCtaBorderColor: options.headerGalleryLinkCtaColor,
          headerGalleryLinkCtaButtonStyle: options.headerGalleryLinkCtaShape,
          headerGalleryLinkCtaTextColor: options.headerGalleryLinkCtaColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        return {
          headerGalleryLinkCtaBackgroundColor: 'transparent',
          headerGalleryLinkCtaBorderColor: options.headerGalleryLinkCtaColor,
          headerGalleryLinkCtaButtonStyle: CtaButtonStyle.TEXT,
          headerGalleryLinkCtaTextColor: options.headerGalleryLinkCtaColor,
        };
      default:
        return {};
    }
  }

  private getHoverCtaStyleOptions(options) {
    switch (options.hoverCtaStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // hoverCtaTextColor field only used for filled buttons
        return {
          hoverCtaBackgroundColor: options.hoverCtaColor,
          hoverCtaBorderColor: options.hoverCtaColor,
          hoverCtaButtonStyle: options.hoverCtaShape,
          hoverCtaTextColor: options.hoverCtaTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use hoverCtaColor for text and border colors
        return {
          hoverCtaBackgroundColor: 'transparent',
          hoverCtaBorderColor: options.hoverCtaColor,
          hoverCtaButtonStyle: options.hoverCtaShape,
          hoverCtaTextColor: options.hoverCtaColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        // Hover CTA does not have an underline for Text Only button
        return {
          hoverCtaBackgroundColor: 'transparent',
          hoverCtaBorderColor: 'transparent',
          hoverCtaButtonStyle: CtaButtonStyle.TEXT,
          hoverCtaTextColor: options.hoverCtaColor,
        };
      default:
        return {};
    }
  }

  private getModalAddToCartCtaStyleOptions(options) {
    switch (options.primaryButtonStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // primaryButtonTextColor field only used for filled buttons
        return {
          modalAddToCartBackgroundColor: options.primaryButtonColor,
          modalAddToCartBorderColor: options.primaryButtonColor,
          modalAddToCartButtonStyle: options.primaryButtonShape,
          modalAddToCartTextColor: options.primaryButtonTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use primaryButtonColor for text and border colors
        return {
          modalAddToCartBackgroundColor: 'transparent',
          modalAddToCartBorderColor: options.primaryButtonColor,
          modalAddToCartButtonStyle: options.primaryButtonShape,
          modalAddToCartTextColor: options.primaryButtonColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        return {
          modalAddToCartBackgroundColor: 'transparent',
          modalAddToCartBorderColor: options.primaryButtonColor,
          modalAddToCartButtonStyle: CtaButtonStyle.TEXT,
          modalAddToCartTextColor: options.primaryButtonColor,
        };
      default:
        return {};
    }
  }

  private getModalAddToCartViewMoreCtaStyleOptions(options) {
    switch (options.secondaryButtonStyle) {
      case FanreelCustomizationCtaType.FILLED:
        // secondaryButtonTextColor field only used for filled buttons
        return {
          modalAddToCartViewMoreBackgroundColor: options.secondaryButtonColor,
          modalAddToCartViewMoreBorderColor: options.secondaryButtonColor,
          modalAddToCartViewMoreButtonStyle: options.secondaryButtonShape,
          modalAddToCartViewMoreTextColor: options.secondaryButtonTextColor,
        };
      case FanreelCustomizationCtaType.OUTLINED:
        // Outlined buttons use secondaryButtonColor for text and border colors
        return {
          modalAddToCartViewMoreBackgroundColor: 'transparent',
          modalAddToCartViewMoreBorderColor: options.secondaryButtonColor,
          modalAddToCartViewMoreButtonStyle: options.secondaryButtonShape,
          modalAddToCartViewMoreTextColor: options.secondaryButtonColor,
        };
      case FanreelCustomizationCtaType.TEXT:
        return {
          modalAddToCartViewMoreBackgroundColor: 'transparent',
          modalAddToCartViewMoreBorderColor: options.secondaryButtonColor,
          modalAddToCartViewMoreButtonStyle: CtaButtonStyle.TEXT,
          modalAddToCartViewMoreTextColor: options.secondaryButtonColor,
        };
      default:
        return {};
    }
  }

  // Given overlay and text colors, determine if they represent a given theme, or
  // are custom values.
  private getThemeForColors(overlay: string, text: string) {
    if (overlay === HoverThemeColors.BLACK && text === HoverThemeColors.WHITE) {
      return HoverStateTheme.DARK;
    } else if (
      overlay === HoverThemeColors.WHITE &&
      text === HoverThemeColors.BLACK
    ) {
      return HoverStateTheme.LIGHT;
    } else {
      return HoverStateTheme.CUSTOM;
    }
  }

  private checkGalleryThumbnailAspectRatio(options): boolean {
    const isMasonryLayout = options.layout === ExperienceLayoutType.MASONRY;
    const isSquareCropped =
      options.galleryThumbnailImageAspectRatio ===
      FanreelCustomizationImageAspectRatios.SQUARE;

    return !isMasonryLayout && isSquareCropped;
  }

  private computeUpdateRequestFromState(
    options,
    containerId: ExperienceContainerId,
    containerType?: ExperienceContainerType,
  ) {
    const isDataSource =
      options.mediaSource.indexOf(Constants.DataSourcePrefix) === 0;

    const themeColors = this.getColorsForTheme(
      options.hoverStateColor,
      options.hoverOverlayColor,
      options.hoverTextColor,
    );
    const carouselNavigationStyleOptions =
      this.getCarouselNavigationStyleOptions(options);
    const galleryThumbnailImageIsSquare =
      this.checkGalleryThumbnailAspectRatio(options);
    const headerGalleryLinkCtaStyleOptions =
      this.getHeaderGalleryLinkCtaStyleOptions(options);
    const headerUploaderCtaStyleOptions =
      this.getHeaderUploaderCtaStyleOptions(options);
    const hoverCtaStyleOptions = this.getHoverCtaStyleOptions(options);
    const loadMoreStyleOptions = this.getLoadMoreStyleOptions(options);
    const modalAddToCartCtaStyleOptions = options.showAddToCart
      ? this.getModalAddToCartCtaStyleOptions(options)
      : {};

    const modalAddToCartViewMoreCtaStyleOptions = options.showAddToCart
      ? this.getModalAddToCartViewMoreCtaStyleOptions(options)
      : {};

    const derivedModalTextColor = (() => {
      const shouldBeDark = shouldUseDarkText(options.modalBackgroundColor);
      if (isUndefined(shouldBeDark)) {
        return undefined;
      }

      // These colors are used in the consumer experience so we dont want to reference theme values
      return shouldBeDark ? ModalColors.BLACK : ModalColors.WHITE;
    })();

    const newOptions: any = {
      customizationOptions: {
        font: options.font,
        hoverCtaBackgroundColor: hoverCtaStyleOptions.hoverCtaBackgroundColor,
        hoverCtaBorderColor: hoverCtaStyleOptions.hoverCtaBorderColor,
        hoverCtaButtonStyle: hoverCtaStyleOptions.hoverCtaButtonStyle,
        hoverCtaTextColor: hoverCtaStyleOptions.hoverCtaTextColor,
        hoverStateColor: undefined,
        hoverOverlayColor: themeColors.overlay,
        hoverTextColor: themeColors.text,

        // Hover state in NEF is stored separately for gallery and carousel layouts. At the time
        // of writing, these options were the same, but it's possible for gallery and carousel
        // layouts to support different sets of hover state options.
        galleryHoverShouldRoundLikes: options.galleryHoverShouldRoundLikes,
        galleryHoverShowCta: options.galleryHoverShowCta,
        galleryHoverShowInstagramIcon: options.galleryHoverShowInstagramIcon,
        galleryHoverShowLikes: options.galleryHoverShowLikes,
        galleryHoverShowPlusIcon: options.galleryHoverShowPlusIcon,
        galleryHoverShowUser: options.galleryHoverShowUser,
        galleryThumbnailImageIsSquare,
        galleryThumbnailShowVideoIcon: options.galleryThumbnailShowVideoIcon,
        carouselHoverShouldRoundLikes: options.carouselHoverShouldRoundLikes,
        carouselHoverShowCta: options.carouselHoverShowCta,
        carouselHoverShowInstagramIcon: options.carouselHoverShowInstagramIcon,
        carouselHoverShowLikes: options.carouselHoverShowLikes,
        carouselHoverShowPlusIcon: options.carouselHoverShowPlusIcon,
        carouselHoverShowUser: options.carouselHoverShowUser,
        carouselNavigationBackgroundColor:
          carouselNavigationStyleOptions.carouselNavigationBackgroundColor,
        carouselNavigationBorderColor:
          carouselNavigationStyleOptions.carouselNavigationBorderColor,
        carouselNavigationIconColor: options.carouselNavigationIconColor,
        carouselNavigationIconStyle:
          carouselNavigationStyleOptions.carouselNavigationIconStyle,
        carouselNavigationPosition: options.carouselNavigationPosition,
        carouselThumbnailImageIsSquare:
          options.layout === ExperienceLayoutType.VIDEO_COMMERCE
            ? false
            : options.carouselThumbnailImageAspectRatio ===
              FanreelCustomizationImageAspectRatios.SQUARE,
        // Only include carouselThumbnailShowCaption if the layout is Video Commerce
        ...(options.layout === ExperienceLayoutType.VIDEO_COMMERCE
          ? {
              carouselThumbnailShowCaption:
                options.carouselThumbnailShowCaption === undefined
                  ? true
                  : options.carouselThumbnailShowCaption,
              modalBackgroundColor: options.modalBackgroundColor,
              modalBvLogoColor: derivedModalTextColor,
              modalEcommerceTitleColor: derivedModalTextColor,
              modalScrollHintTextColor: derivedModalTextColor,
            }
          : {
              // Explicitly set carouselThumbnailShowCaption to false for non-VIDEO_COMMERCE layouts
              carouselThumbnailShowCaption: false,
            }),
        // Always enable the video icon in the Video Commerce layout
        carouselThumbnailShowVideoIcon:
          options.layout === ExperienceLayoutType.VIDEO_COMMERCE
            ? true
            : options.carouselThumbnailShowVideoIcon,
        modalTextColor: options.modalTextColor,
        iconColor: options.iconColor,
        ctaButtonColor: options.ctaButtonColor,
        ctaTextColor: options.ctaTextColor,
        headerTextColor: options.headerTextColor,
        headerText: options.showTitle ? options.headerText : '',
        headerSubtext: options.showSubtitle ? options.headerSubtext : '',
        showGalleryLink: options.showGalleryLink,
        galleryLinkCta: options.showGalleryLink ? options.galleryLinkCta : '',
        galleryLinkUrl: options.showGalleryLink ? options.galleryLinkUrl : '',
        showHeader: !!(
          options.showTitle ||
          options.showSubtitle ||
          options.enableUploader ||
          options.showGalleryLink
        ),
        showTitle: options.showTitle,
        headerCtaPosition: options.headerCtaPosition,
        headerFontSize:
          options.headerFontSize ||
          this.ExperienceCustomizationDefaults.HEADER_FONT_SIZE,
        showSubtitle: options.showSubtitle,
        subheaderFontSize:
          options.subheaderFontSize ||
          this.ExperienceCustomizationDefaults.SUBHEADER_FONT_SIZE,
        subheaderTextColor: options.subheaderTextColor,
        showUploaderCta: options.enableUploader, // force true for now if uploader is enabled
        uploaderCtaText: options.uploaderCtaText,
        includeEcommerceMetadata: options.includeEcommerceMetadata,
        showBrandName: options.showBrandName,
        showCaption: options.showCaption,
        showLikeCount: options.showLikeCount,
        showReportMedia: options.showReportMedia,
        showTimestamp: options.showTimestamp,
        showUser: options.showUser,
        modalHeroVideoAutoplay: options.modalHeroVideoAutoplay ?? true,
        showShareIcons: options.showShareIcons,
        showProductTags: options.showProductTags,
        productDisplayStyle: options.productDisplayStyle,
        modalHeroIsSquare:
          options.modalImageAspectRatio ===
          FanreelCustomizationImageAspectRatios.SQUARE,
        ...(options.layout === ExperienceLayoutType.VIDEO_COMMERCE
          ? {
              modalEcommerceProductPriceColor:
                options.modalEcommerceProductPriceColor,
              modalEcommerceProductSalePriceColor:
                options.modalEcommerceProductSalePriceColor,
              modalEcommerceShopButtonColor:
                options.modalEcommerceShopButtonColor,
              modalEcommerceShopButtonTextColor:
                options.modalEcommerceShopButtonTextColor,
              modalHeroProgressBarInProgressBackground:
                options.modalHeroProgressBarInProgressBackground,
            }
          : {}),
        modalType: 'Modal', // options.modalType,
        itemTrayTitle: options.itemTrayTitle,
        ctaText:
          options.ctaText || this.ExperienceCustomizationDefaults.CTA_TEXT,
        hoverText: options.hoverText,
        // In the case of Like2Buy (non-reel backed experiences) we just disable report media
        // both because this doesn't make sense for Like2Buy content, and that it won't work
        // currently on the backend without a reel ID.
        enableReportMedia: !isDataSource && options.showReportMedia,
        reportMediaReelId: options.mediaSource,
        enableUploader: !isDataSource && options.enableUploader,
        uploaderReelId: options.mediaSource,
        uploaderTermsAndConditionsLink: options.uploaderTermsAndConditionsLink,
        sort: options.sort,
        filterLabelSet: options.filterLabelSet,
        numberOfRows: options.numberOfRows,
        minNumberOfColumns: options.minNumberOfColumns,
        numberOfColumns: options.numberOfColumns,
        gutterSize: options.gutterSize,
        loadMoreBackgroundColor: loadMoreStyleOptions.loadMoreBackgroundColor,
        loadMoreBorderColor: loadMoreStyleOptions.loadMoreBorderColor,
        loadMoreButtonStyle: loadMoreStyleOptions.loadMoreButtonStyle,
        loadMoreTextColor: loadMoreStyleOptions.loadMoreTextColor,
        loadMoreCtaText: options.loadMoreCtaText,
        localizationStrings: options.localizationStrings,
        headerGalleryLinkCtaBackgroundColor:
          headerGalleryLinkCtaStyleOptions.headerGalleryLinkCtaBackgroundColor,
        headerGalleryLinkCtaBorderColor:
          headerGalleryLinkCtaStyleOptions.headerGalleryLinkCtaBorderColor,
        headerGalleryLinkCtaButtonStyle:
          headerGalleryLinkCtaStyleOptions.headerGalleryLinkCtaButtonStyle,
        headerGalleryLinkCtaTextColor:
          headerGalleryLinkCtaStyleOptions.headerGalleryLinkCtaTextColor,
        headerUploaderCtaBackgroundColor:
          headerUploaderCtaStyleOptions.headerUploaderCtaBackgroundColor,
        headerUploaderCtaBorderColor:
          headerUploaderCtaStyleOptions.headerUploaderCtaBorderColor,
        headerUploaderCtaButtonStyle:
          headerUploaderCtaStyleOptions.headerUploaderCtaButtonStyle,
        headerUploaderCtaTextColor:
          headerUploaderCtaStyleOptions.headerUploaderCtaTextColor,
        modalAddToCartIsEnabled: options.showAddToCart,
        ...(options.showAddToCart
          ? {
              modalAddToCartBackgroundColor:
                modalAddToCartCtaStyleOptions.modalAddToCartBackgroundColor,
              modalAddToCartBorderColor:
                modalAddToCartCtaStyleOptions.modalAddToCartBorderColor,
              modalAddToCartButtonStyle:
                modalAddToCartCtaStyleOptions.modalAddToCartButtonStyle,
              modalAddToCartTextColor:
                modalAddToCartCtaStyleOptions.modalAddToCartTextColor,
              modalAddToCartButtonText: options.primaryButtonText,

              modalAddToCartViewMoreBackgroundColor:
                modalAddToCartViewMoreCtaStyleOptions.modalAddToCartViewMoreBackgroundColor,
              modalAddToCartViewMoreBorderColor:
                modalAddToCartViewMoreCtaStyleOptions.modalAddToCartViewMoreBorderColor,
              modalAddToCartViewMoreButtonStyle:
                modalAddToCartViewMoreCtaStyleOptions.modalAddToCartViewMoreButtonStyle,
              modalAddToCartViewMoreTextColor:
                modalAddToCartViewMoreCtaStyleOptions.modalAddToCartViewMoreTextColor,
              modalAddToCartViewMoreButtonText: options.secondaryButtonText,
              platformType: options.ecommercePlatformType,
              salesforceSiteId: options.salesforceSiteId,
            }
          : {}),
      },
      layout: options.layout,
      source: options.mediaSource,
      dataSourceId:
        options.dataSourceId ||
        (isDataSource
          ? options.mediaSource.substring(Constants.DataSourcePrefix.length)
          : null),
      containerId,
    };

    if (options.locale) {
      newOptions.locale = options.locale;
    }

    if (containerType) {
      newOptions.containerType = containerType;
    }

    return newOptions;
  }
}

angular
  .module('curalate')
  .service('FanreelCustomizationService', [
    '$q',
    '$log',
    'ErrorNotificationService',
    'ExperiencesApiService',
    'ExperienceCustomizationDefaults',
    'ExperiencesService',
    'SessionVarsService',
    'GalleriesApi',
    'rx',
    '$ngRedux',
    FanreelCustomizationService,
  ]);
